import { FC, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { RelatedType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { SendInThreadModal, ThreadAttachedAsset } from '@frontend/integrated-messaging';
import { useMessagingFeaturesEnabled } from '@frontend/messaging-enabled-hook';
import { useAppFlagStore } from '@frontend/shared';
import { pendoTags } from '../../../../../constants/pendoTags';
import { useAppointmentInfo } from '../../../../../hooks/useAppointmentInfo';
import { useSendFormsContext } from '../../../../../providers/context';
import { useSendFormsStore } from '../../../../../providers/store';
import { finishSendFormTracking } from '../../../../../utils/tracker/tracker.utils';
import DeliveryOption from '../delivery-option/delivery-option.component';

const SendAsMessage: FC = () => {
  const { t } = useTranslation('digital-forms-sending-experience');
  const [showSendInThreadModal, setShowSendInThreadModal] = useState(false);
  const { origin, sendFormsModalControls } = useSendFormsContext();
  const {
    personId,
    formOrPacketToSend,
    showMessageComponent,
    solicitedLink,
    appointmentInfo,
    temporarilyHideModal,
    selectedDeliveryMethod,
    locationId,
    submissionToken,
    acceptedFiles,
    clearStore,
  } = useSendFormsStore([
    'personId',
    'formOrPacketToSend',
    'showMessageComponent',
    'clearStore',
    'appointmentInfo',
    'solicitedLink',
    'temporarilyHideModal',
    'selectedDeliveryMethod',
    'locationId',
    'submissionToken',
    'acceptedFiles',
    'setAcceptedFiles',
  ]);

  const attachedAssets: ThreadAttachedAsset[] = useMemo(
    () =>
      acceptedFiles.map((file) => ({
        id: file.id,
        name: file.file.name,
      })),
    [acceptedFiles]
  );

  const wasModalRestoredRef = useRef(false);
  const { appointmentAt } = useAppointmentInfo();
  const { featureFlags } = useAppFlagStore();
  const messagingEnabled = useMessagingFeaturesEnabled();
  const canSendMessages = featureFlags[locationId]?.get('nwx:messaging-integrated-messaging-fe-acl-enforcement')?.value
    ? hasSchemaACL(locationId, Permission.SMS_INBOX_MANAGE)
    : true;
  const messageHoverLabel = !messagingEnabled
    ? t('Messaging is disabled for this location')
    : !canSendMessages
    ? t('You do not have permission to send text messages')
    : undefined;
  const { closeModal: closeSendFormsModal } = sendFormsModalControls;

  const openSendInThreadModalHandler = useCallback(() => {
    if (!formOrPacketToSend || !formOrPacketToSend.id) {
      return;
    }

    setShowSendInThreadModal(true);
  }, [formOrPacketToSend]);

  const closeMessageThreadModalHandler = useCallback(() => {
    setShowSendInThreadModal(false);
  }, []);

  const onSendMessageHandler = useCallback(() => {
    finishSendFormTracking({
      deliveryMethod: 'message',
      isComplete: true,
      origin,
    });
    closeSendFormsModal();
    clearStore(origin);
  }, [origin, closeSendFormsModal, clearStore]);

  // Restore the modal if it was temporarily hidden
  useEffect(() => {
    if (selectedDeliveryMethod !== 'message' || wasModalRestoredRef.current || showSendInThreadModal) {
      return;
    }

    if (!temporarilyHideModal) {
      openSendInThreadModalHandler();
      wasModalRestoredRef.current = true;
    }
  }, [temporarilyHideModal, selectedDeliveryMethod, showSendInThreadModal, openSendInThreadModalHandler]);

  return (
    <>
      <DeliveryOption
        icon='messaging-small'
        displayName={t('Share in Message')}
        deliveryMethod='message'
        onClick={openSendInThreadModalHandler}
        show={showMessageComponent}
        trackingId={pendoTags.steps.deliveryMethod.direct.via.sms}
        disabled={!messagingEnabled || !canSendMessages}
        hoverLabel={messageHoverLabel}
      />

      <SendInThreadModal
        show={showSendInThreadModal}
        onClose={closeMessageThreadModalHandler}
        onBack={closeMessageThreadModalHandler}
        onSend={onSendMessageHandler}
        groupId={locationId}
        personId={personId}
        templateType={TemplateType_Slug.MANUAL_FORMS_MESSAGE}
        linkData={{
          link: solicitedLink,
          relatedId: submissionToken,
          relatedType: RelatedType.RELATED_TYPE_FORM,
        }}
        contextPreviewProps={{
          iconName: 'forms',
          title: formOrPacketToSend?.name ?? '',
          subtitle:
            !!appointmentInfo && `${appointmentAt}, ${appointmentInfo?.practitioner}, ${appointmentInfo?.pmsType}`,
        }}
        propertyBindings={{
          EVENT_AT: appointmentInfo?.appointmentAt || undefined,
        }}
        attachedAssets={attachedAssets}
      />
    </>
  );
};

export default SendAsMessage;
